import { React, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import track from "../lib/analytics/events";
import { useReferrer } from "../hooks/useReferrer";
import {
  OwnliButton,
  OwnliCard,
  OwnliHeader,
} from "./ownli/OwnliScreenWrapper";

const WelcomeScreen = ({ userData, onComplete, isReturningUser }) => {
  const { displayConfig, isLoading: referrerLoading } = useReferrer();
  const partnerName = referrerLoading ? undefined : displayConfig.partnerName;
  const [showOwnliModal, setShowOwnliModal] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);

  // Extract user name if available (user may not exist yet)
  const getUserName = () => {
    if (userData?.firstName) return userData.firstName;

    try {
      const storedUserData = sessionStorage.getItem("userData");
      if (storedUserData) {
        const parsed = JSON.parse(storedUserData);
        if (parsed?.firstName) return parsed.firstName;
      }
    } catch (e) {
      console.error("Error parsing stored user data:", e);
    }

    return null;
  };

  // // Journey steps
  // const steps = [
  //   {
  //     title: "Answer a few questions about how you protect your home.",
  //   },
  //   {
  //     title: "Confirm Your Setup (if relevant).",
  //   },
  //   {
  //     title: "Earn Your Reward.",
  //   },
  // ];

  // Check if user has existing progress
  const hasSurveyData = sessionStorage.getItem("surveyData");
  const hasUserData = sessionStorage.getItem("userData");
  const surveyData = hasSurveyData ? JSON.parse(hasSurveyData) : {};
  const isCompletedSurvey = Object.keys(surveyData).length >= 3;
  const userName = getUserName();

  return (
    <div className="p-4 pt-5 pb-6 stagger-children">
      {/* Welcome Header */}

      {/* Personalized Greeting */}
      {userName && (
        <div className="text-center mb-3">
          <p className="text-lg font-semibold text-gray-800">
            Welcome, {userName}!
          </p>
        </div>
      )}
      <OwnliHeader
        title="YOUR HOME SAFETY REWARD"
        subtitle="Powered by Nationwide built in partnership with Ownli."
        titleClass="!text-lg"
        subTitleClass="!text-sm"
        style={{ background: "transparent", boxShadow: "none", borderRadius: 0 }}
      >
        <Swiper
          modules={[Autoplay, Pagination]}
          pagination={{ clickable: true }}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            stopOnLastSlide: true,
          }}
          loop={false}
          onTouchStart={(swiper) => {
            if (!userInteracted) {
              setUserInteracted(true);
              swiper.autoplay?.stop();
            }
          }}
          onReachEnd={(swiper) => {
            // Auto-rotate: rest on last slide then rewind to first
            if (!userInteracted) {
              setTimeout(() => {
                swiper.slideTo(0, 800);
                swiper.autoplay?.stop();
              }, 3000);
            }
          }}
          onTouchEnd={(swiper) => {
            // User swipes forward on last slide — wrap to first
            if (userInteracted && swiper.isEnd && swiper.swipeDirection === "next") {
              setTimeout(() => swiper.slideTo(0, 400), 50);
            }
          }}
          spaceBetween={0}
          slidesPerView={1}
          className="welcome-carousel"
          style={{ paddingBottom: "2rem" }}
        >
          <SwiperSlide>
            <div className="flex flex-col items-center px-4">
              <img src="/carousel-survey.png" alt="Answer questions" className="max-h-48 object-contain mb-3" />
              <p className="text-sm text-gray-700 text-center">
                You'll be asked to answer a few questions and upload a picture, if you have a security system.
              </p>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="flex flex-col items-center px-4">
              <img src="/carousel-confirm.png" alt="Confirm your setup" className="max-h-48 object-contain mb-3" />
              <p className="text-sm text-gray-700 text-center">
                We'd also like to hear from you if you don't (no picture necessary).
              </p>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="flex flex-col items-center px-4">
              <img src="/carousel-reward.png" alt="Earn your reward" className="max-h-48 object-contain mb-3" />
              <p className="text-sm text-gray-700 text-center">
                As a thank you for taking our survey, you can earn up to a $10 reward that you can redeem for a gift card.
              </p>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="flex flex-col items-center px-4">
              <img src="/walletv1.png" alt="Home safety reward" className="max-h-48 object-contain mb-3" />
              <p className="text-sm text-gray-700 text-center">
                Claim your reward with an Amazon or Visa gift card.
              </p>
            </div>
          </SwiperSlide>
        </Swiper>
      </OwnliHeader>
      {/* Engagement Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center mt-4">
        <p className="text-sm font-medium text-blue-900">
          Takes less than 60 seconds to claim
        </p>
      </div>
      <OwnliCard className="bg-white border-none rounded-[20px] !mt-0">
        <div>
          {/* <h2 className="text-base font-bold mb-4">Here's what to expect:</h2>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="mt-[6px] min-w-[10px] min-h-[10px] bg-blue-600/50 rounded-full"></span>
                <div>
                  <h3 className="font-base text-darkGrey">{step.title}</h3>
                </div>
              </div>
            ))}
          </div> */}

          {/* Ownli Learn More Link */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Why Ownli? We're an independent partner helping you keep your data
              secure and your rates low.{" "}
              <button
                onClick={() => setShowOwnliModal(true)}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Learn More
              </button>
            </p>
          </div>
        </div>
      </OwnliCard>

      {/* Ownli Modal */}
      {showOwnliModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-3">Why you can trust Ownli:</h3>
            <p className="text-gray-700 text-sm mb-4">
              Nationwide partners with us because we specialize in secure,
              private data verification. We act as a "neutral zone", ensuring
              your details are accurate so you get your discount, while keeping
              your personal data under your control. With Ownli, you aren't just
              a policy number; you're the owner of your information.
            </p>
            <button
              onClick={() => setShowOwnliModal(false)}
              className="w-full bg-crimsonGlory text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="space-y-3">
        <OwnliButton
          onClick={() => {
            track.welcomeContinueClicked({
              is_returning_user: isReturningUser,
              partner_name: partnerName,
              button_text: isReturningUser
                ? "Continue to Personal Information"
                : "CLAIM MY REWARD",
            });
            onComplete();
          }}
          className="w-full"
        >
          {isReturningUser
            ? "Continue to Personal Information"
            : "CLAIM MY REWARD"}
          <ArrowRight className="w-4 h-4" />
        </OwnliButton>

        {/* Skip to Verification button for users who completed survey */}
        {!isReturningUser && isCompletedSurvey && !hasUserData && (
          <OwnliButton
            onClick={() => {
              track.welcomeSkipToVerificationClicked({
                has_survey_data: isCompletedSurvey,
                has_user_data: !!hasUserData,
              });
              sessionStorage.setItem("skipToVerification", "true");
              onComplete();
            }}
            variant="outline"
            className="w-full"
          >
            Skip to Verification
          </OwnliButton>
        )}

        <div className="text-center text-sm text-gray-600">
          By continuing, you agree to Ownli's{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 underline inline-flex items-center"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011276138-Ownli-Privacy-Policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 underline inline-flex items-center"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
